'use client'

import { Check, Share } from 'lucide-react'
import { useState } from 'react'
import { Button, type ButtonProps } from '@/src/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/src/components/ui/tooltip'
import { useGetReferrerProfile } from '@/src/queries/hooks/referralcodes'
import { cn } from '@/src/utils/styles'
import { successToast } from '../ui/toast'

interface ShareButtonProps {
  className?: string
  buttonProps?: ButtonProps
}

export const useShareButton = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const { data: referrerProfile } = useGetReferrerProfile()

  const handleShare = async (showToast?: boolean) => {
    try {
      const url = new URL(window.location.href)

      // Convert query param format to path-based format for cleaner sharing URLs
      // E.g., /tokens?ticker=BTC → /tokens/BTC
      if (url.pathname === '/tokens' && url.searchParams.has('ticker')) {
        const ticker = url.searchParams.get('ticker')
        if (ticker) {
          url.pathname = `/tokens/${ticker}`
          url.searchParams.delete('ticker')
        }
      }

      if (referrerProfile?.referral_code) {
        url.searchParams.set('referrer', referrerProfile.referral_code)
      }

      await navigator.clipboard.writeText(url.toString())

      if (showToast) {
        successToast('Copied to clipboard')
      }

      setIsTooltipOpen(true)

      // Hide tooltip after 2 seconds
      setTimeout(() => {
        setIsTooltipOpen(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error)
    }
  }

  return {
    isTooltipOpen,
    handleShare,
    setIsTooltipOpen,
  }
}

export function ShareButton({ className, buttonProps }: ShareButtonProps) {
  const { isTooltipOpen, handleShare, setIsTooltipOpen } = useShareButton()

  return (
    <TooltipProvider disableHoverableContent delayDuration={999999}>
      <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger asChild>
          <Button
            variant='secondary'
            size='md'
            onClick={() => handleShare()}
            {...buttonProps}
            className={cn(
              'p-3 button-secondary-text hover:text-primary',
              className,
              buttonProps?.className,
            )}
          >
            <Share className='w-6 h-6' />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side='top'
          className='bg-surface2-100 text-primary border-border1 p-3 flex items-center gap-3'
        >
          <div className='flex items-center justify-center rounded-2xl'>
            <Check className='w-4 h-4 text-success-text' />
          </div>
          <p className='text-xs font-semibold whitespace-nowrap'>
            Page link copied to clipboard
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
